import { preferenceKey } from '../lib/feedPreferences';
import { PREDICT_FEED, RU_FEED, TRANSFERS } from '../lib/feedContext';
import { sportCanon } from '../lib/sports';
/**
 * Профиль ленты: то, что человек настроил в студии и чем лента обязана жить.
 *
 * Схема здесь одна на всех. Раньше она существовала объектом `D` внутри
 * `studio/engine.js` — без типа, без хранилища и без адреса: настроенное умирало
 * по F5, а строка обмена профилями была надписью, которую некому прочитать.
 * Неработающая студия хуже её отсутствия: она обещает управление лентой и не
 * даёт его, и человек списывает на «не разобрался» то, чего в продукте нет.
 *
 * Три вещи, которые этот файл держит вместе:
 *
 *   тип       одно описание профиля для студии и для ленты;
 *   хранилище тот же `localStorage`, что у настроек — профиль переживает F5;
 *   строка    `forq1:` — обмен профилями, разбираемый той же парой функций.
 *
 * Кодирование и разбор строки живут РЯДОМ намеренно. Разъехавшаяся пара — это
 * профиль, который отдали товарищу, а он открылся другим: ошибка тихая, видна
 * только тому, кто сверит две ленты вручную.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Ручек, под которыми у зеркала нет данных (глубина стакана,
 * минимум и комиссия биржи, наша сверка цены, перестановки исходов, возраст
 * цены в секундах), в профиле нет вовсе — их же убирает и студия (`GONE_KNOBS`).
 * Сохранённая ручка, которая ни на что не влияет, обучает не смотреть на
 * остальные.
 */

import { create } from 'zustand';

import { UNNAMED_VENUE, type Fork, marketFamily } from '../lib/protocol';
import { isStretchPeriod, marketPeriod, periodFamily } from '../lib/sports';
import { asPolyOrder, type PolyOrder } from '../poly/market';
import { useSettings, SETTINGS_KEY, isRoundStep, settingsRecord, type RoundStep } from '../store/settings';
import { ALARM_KEY, alarmRecord, useAlarm, toneOf, scaleOf, volumeOf, type ToneId, type ScaleId } from './alarm';

/** Как читать список «моих контор»: по обеим ногам или хотя бы по одной. */
export type Strict = 'both' | 'either';

/** Что делать со строкой, на которой сработала проверка. */
export type RulePos = 'hide' | 'mark' | 'off';

/** Требование ко входу: не нужен, хотя бы на одной ноге, на обеих. */
export type LinkNeed = 'no' | 'either' | 'both';

/** Точность входа по лестнице студии: купон исхода, страница матча, площадка. */
export type Precision = 'any' | 'coupon' | 'match' | 'site';

/** Какую ленту показывать. */
export type LineMode = 'both' | 'live' | 'pre';

/** Порядок ленты. Совпадает с ключами сортировки сита. Старый `roi` → деньги. */
export type ProfileSort = 'money' | 'age' | 'life';

export interface Profile {
  /** Settings owned by Studio, persisted with the same Apply operation. */
  audio?: { volume: number; tone: ToneId; scale: ScaleId };
  /**
   * Имя. Видно в шапке ленты — и это не украшение: забытый профиль выглядит
   * как спокойный рынок, а спрашивают за это сканер.
   */
  name: string;

  // ── Деньги ────────────────────────────────────────────────────────────────
  /** Банк на одну вилку, рубли. */
  bank: number;
  /** Рублей за доллар: биржевая нога считается по нему. */
  fx: number;
  /** Секунд на вторую ставку. Ноль — не замерено, темп берёт лента. */
  tempo: number;
  /** Округлять конторскую ногу до десятков, сотен или тысяч. */
  roundStep: RoundStep;
  /** Вниз — не вылезем за банк. */
  roundDir: 'down' | 'near';
  /** Кто получает круглую сумму: контора или площадка с грубым шагом. */
  roundWho: 'book' | 'coarse';
  /** Не показывать вилки дешевле, рубли ожидаемой прибыли. */
  minRub: number;
  /** Нижняя и верхняя границы процента. Ноль — граница не поставлена. */
  minRoi: number;
  maxRoi: number;

  // ── Кого показывать ───────────────────────────────────────────────────────
  /** Только те вилки, где стоят мои конторы. */
  mine: boolean;
  strict: Strict;
  /** Отмеченные конторы: код площадки → отмечена ли. */
  books: Record<string, boolean>;
  /** Положение каждой проверки: код → прячет / помечает / молчит. */
  rules: Record<string, RulePos>;

  /** Сколько прибыли в минуту при вашем темпе. Ноль — не спрашиваем. */
  perMin: number;
  /**
   * Граница хвоста, рубли: мельче — вниз списком, но не из ленты. Это порог
   * ленты (`settings.gate`), а не второе его имя: профиль его переносит, чтобы
   * применённый профиль не оставлял ленту с чужой границей яруса.
   */
  tailRub: number;
  /** Сколько денег вилка обязана принять. Ноль — не спрашиваем. */
  minFit: number;
  /** Требовать названный площадкой потолок вместо допущенного оператором. */
  capKind: 'any' | 'known';
  /** Валюты ног: любые, хотя бы одна зарубежная, только зарубежные, только рубли. */
  curKind: 'any' | 'mix' | 'out' | 'rub';

  // ── Успею ли ──────────────────────────────────────────────────────────────
  /** Убирать лайв-строки старше, секунды. Ноль — не спрашиваем. */
  hardKill: number;
  /**
   * Убирать прематч старше, секунды. На странице прематч не применяется:
   * возраст прематча не спрашиваем. Поле живёт, чтобы старый профиль не
   * ломал чтение; ноль и любое число на прематче одно и то же.
   */
  hardKillPre: number;
  /**
   * Живучесть: сколько такая вилка держится ОБЫЧНО, по замеру сервера (`sv`).
   * Ноль — не спрашиваем. Пришло на место `live15`/`live30` — долей «шанс
   * дожить 15 секунд» наш движок не считает, и обе ручки на этой ленте молчали.
   */
  life: number;
  /** Цена стоит не дольше, секунды. Ноль — не спрашиваем. */
  still: number;
  /** Что делать со строкой, где покой не измерен, а оценён снизу. */
  stillSure: 'show' | 'hide';

  // ── Куда нажимать ─────────────────────────────────────────────────────────
  link: LinkNeed;
  prec: Precision;
  /**
   * Порядок ног на карточке. `auto` — кто может не встать (и исключения площадок).
   * `poly` — полимаркет всегда первой ногой.
   */
  legsFirst: 'auto' | 'poly';
  /**
   * Нога Polymarket. База — лимитка. `market` — доп. режим: доллары по стакану.
   */
  polyOrder: PolyOrder;

  // ── Согласен ли рынок ─────────────────────────────────────────────────────
  /** Сколько независимых площадок должно подтверждать цену. Ноль — не спрашиваем. */
  witness: number;
  /** Допустимое превышение над рынком, п.п. Ноль — не спрашиваем. */
  edgePp: number;
  /** Требовать право вести: всё проверено, живучесть своя, потолок назван. */
  lead: boolean;

  // ── Лента ─────────────────────────────────────────────────────────────────
  liveMode: LineMode;
  sort: ProfileSort;
  /** Обе ноги в одной конторе — почти всегда ошибка матчинга. */
  sameBook: 'hide' | 'show';
  /**
   * Свернуть строки одного матча в одну, самую денежную. Тотал 22.5 и 23.5 —
   * это одна и та же вилка на соседних линиях, и обе ставить нельзя.
   */
  collapse: boolean;
  /**
   * Выключенные дисциплины и семейства рынка — списком ВЫКЛЮЧЕННЫХ, а не
   * разрешённых. Разрешающий список молча спрятал бы дисциплину, которой в
   * ленте ещё не было: человек выключил Dota, а не «всё, кроме сегодняшних».
   */
  offSports: string[];
  offMarkets: string[];
  /**
   * В лайве не брать карту, сет, гейм, четверть, тайм, иннинг. Матч остаётся.
   *
   * Какой отрезок идёт сейчас, площадки не говорят — прячем все такие ставки
   * в лайве, не одну «текущую». Кэфы на идущем отрезке скачут, поймать сложно.
   */
  skipLiveStretch: boolean;
  /**
   * Выключенные семейства отрезка: M матч, P карта, S сет, Q четверть,
   * H тайм, I иннинг. Пусто — все. Режет и лайв, и прематч.
   */
  offSpans: string[];

  // ── Сигнал ────────────────────────────────────────────────────────────────
  /** Звук на новую вилку. */
  sound: boolean;
  /** Уведомление браузера — второй канал того же сигнала, а не его дубль. */
  notify: boolean;
  /**
   * От какой прибыли звать, рубли. Это НЕ порог ленты: рисовать строку крупно и
   * отрывать человека от другого окна — два разных решения.
   */
  alarmRub: number;
  /** Молчать ночью, 02:00–09:00 по часам вкладки. */
  quiet: boolean;
}

/**
 * Положения проверок профиля «работа».
 *
 * Каденцию трубы `stale`/`delayed` не прячем: замер 13.08, прятать stale
 * убило бы 19 из 43 свежих рабочих.
 *
 * `frozen` и `expired` не прячем. 17.09 hide на обоих опустошил ночной
 * прематч: цена там стоит минутами, а `expired` ставит детектор через 50 с.
 * Труп лайва старше 5 минут прячет `statue`. Порог frozen у прематча
 * 5 минут, не 30 секунд: свежая прематч-вилка жива.
 *
 * Несогласие рынка (`no_consensus`) и «некому подтвердить» (`unwitnessed`)
 * не прячем. Три живых кадра 13.08 14:45 МСК (14 / 38 / 49 вилок): hide
 * оставлял 2–4 строки и выкидывал полосу 15–25 %, которую шлёт бот.
 *
 * Прячем арифметику, которую движок и так считает скрытой (`Profile.OWN` на
 * сервере, `/v1/studio`.hides): палп, аномалия кэфа, односторонний стакан,
 * снятая линия, невозможный процент. «Входа нет» — белая пометка.
 */
export const WORK_RULES: Record<string, RulePos> = {
  stale: 'off',
  frozen: 'off',
  expired: 'off',
  statue: 'hide',
  delayed: 'off',
  no_link: 'mark',
  no_consensus: 'mark',
  unwitnessed: 'mark',
  palp: 'hide',
  odds_anomaly: 'mark',
  one_sided: 'hide',
  suspended: 'hide',
  impossible_roi: 'hide',
  crowd_outlier: 'hide',
  // Крипто-сокет тикает чаще трёх секунд — это живая цена, не «не успеть».
  // Прятать дрожание = снять рабочие вилки. Помечаем, не режем.
  flutter: 'mark',
  unwitnessed_kin: 'hide',
  // Пауза двоюродной пары: секундный фантом одной полки, подписью не спасти.
  lag_hold: 'hide',
  // Контора вне рабочего набора: цена честная, но ставить туда ученику нельзя.
  off_set: 'hide',
  draw_possible: 'hide',
};

export const DEFAULT_PROFILE: Profile = {
  name: 'работа',
  bank: 10000,
  roundStep: 100,
  roundDir: 'down',
  roundWho: 'book',
  fx: 81.4,
  tempo: 11,
  // Предикт: шары по центам, банк небольшой — рублёвый порог и потолок 30% там режут честные вилки.
  minRub: PREDICT_FEED || RU_FEED ? 0 : 400,
  minRoi: TRANSFERS ? -5 : 0,
  maxRoi: TRANSFERS ? 1 : PREDICT_FEED || RU_FEED ? 0 : 50,
  mine: false,
  strict: 'both',
  books: {},
  rules: RU_FEED ? {} : { ...WORK_RULES },
  perMin: 0,
  tailRub: 0,
  minFit: 0,
  capKind: 'any',
  // Одна рублёвая нога допустима, обе — нет.
  //
  // Здесь стояло `out` — «ни одной рублёвой». Смысл был в сортировке: пара
  // винлайн×леон выигрывала по рублям у крипты и предикта и занимала верх
  // ленты. Но правило било шире цели. Замер 20.08.2026, три часа, 10 713 вилок:
  // `out` убирал 723 строки, и это были НЕ пары двух рублёвых контор — их в
  // ленте больше нет (leon, winline, zenit выключены; живы olimp и betboom).
  // Убирались связки «рублёвая × зарубежная», то есть ровно те, где рублёвая
  // нога одна и счёт для неё есть.
  //
  // `mix` отклоняет строку, только если рублёвыми оказались ВСЕ ноги, — от чего
  // правило и заводилось. Владелец 20.08: «сделай так, чтобы одна рублевая нога
  // проходила».
  curKind: TRANSFERS || RU_FEED ? 'any' : 'mix',
  // Две минуты. Минута 17-го обнуляла кадр; пять минут держат уже стухшее.
  // Срез 18.08: хвост ≥120 с на крипте/предикт — штуки, треть ≥30% (потолок
  // и так снимет). Полоса 60–120 с — в основном WIN, не дыры HCP.
  // Предикт-вилка на длинном рынке живёт часами; свежесть там — возраст каждой книги.
  hardKill: PREDICT_FEED || RU_FEED ? 0 : 120,
  hardKillPre: PREDICT_FEED || RU_FEED ? 0 : 120,
  life: 0,
  still: 0,
  stillSure: 'show',
  // Ссылок может не быть даже на оба матча. Реальность вилки — не адрес.
  link: 'no',
  prec: 'any',
  legsFirst: 'auto',
  polyOrder: 'limit',
  witness: 0,
  edgePp: 0,
  lead: false,
  liveMode: 'both',
  sort: 'money',
  sameBook: 'hide',
  collapse: !RU_FEED,
  offSports: [],
  offMarkets: [],
  skipLiveStretch: false,
  offSpans: [],
  sound: false,
  notify: false,
  alarmRub: 3000,
  quiet: false,
};

/**
 * Старый дефолт «работа»: пять минут на экране и без потолка процента.
 * Жёстко требовать minRub 200 нельзя — человек мог подвинуть порог денег
 * и навсегда остаться со старым возрастом.
 */
const isLegacyWorkFilter = (p: Profile): boolean =>
  p.name === 'работа' &&
  p.hardKill === 300 &&
  p.minRoi === 0 &&
  p.maxRoi === 0 &&
  p.life === 0 &&
  p.still === 0;

/**
 * Заводская «работа» с чужим возрастом или жёстким входом.
 * Порог денег не трогаем. Ссылку не требуем: её может не быть, вилка при этом
 * настоящая. Возраст — две минуты.
 */
const isOldWorkAge = (p: Profile): boolean =>
  p.name === 'работа' &&
  p.minRoi === 0 &&
  p.maxRoi === 30 &&
  (p.hardKill === 60 ||
    p.hardKill === 300 ||
    p.hardKillPre === 90 ||
    p.hardKillPre === 300 ||
    p.link === 'both' ||
    p.prec === 'match');

/**
 * Заводская «работа» с прежним обращением с рублёвой ногой.
 *
 * Два прежних положения, оба переводятся в `mix`. `any` — заводское до 13.08,
 * когда пара двух рублёвых контор занимала верх ленты по рублям. `out` — правка
 * того же дня, которая цель поразила, но задела и связки с ОДНОЙ рублёвой
 * ногой: их 723 за три часа, и счёт для них у человека есть.
 */
const isWorkRubLegacy = (p: Profile): boolean =>
  p.name === 'работа' &&
  p.minRoi === 0 &&
  p.maxRoi === 30 &&
  (p.curKind === 'any' || p.curKind === 'out');

// ── Хранилище ────────────────────────────────────────────────────────────────

/** Ключ рядом с настройками и фильтрами: одно место, один способ вычистить. */
const KEY = preferenceKey(RU_FEED ? 'forq.next.profile.ru-seven-v1' : 'forq.next.profile.v2');
/** Явно снятый профиль. Пустой ключ и так кран; метка держит «снято» при
 *  миграциях, которые иначе снова записали бы «работу». */
const SKIP_KEY = preferenceKey('forq.next.profile.skip');
/**
 * Один раз поправили обращение с рублёвой ногой на сохранённой «работе». Без
 * метки то же самое повторилось бы после того, как человек выставил своё.
 *
 * Ключ новый, а не прежний `work-fx`: у того он уже проставлен всем, кто открывал
 * ленту после 13.08, и повторный перевод по нему не случился бы ни у кого —
 * то есть у владельца, который на это и пожаловался, в первую очередь.
 */
const WORK_FX_KEY = preferenceKey('forq.next.profile.work-rub-mix');

const num = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const one = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;

const flags = (value: unknown): Record<string, boolean> => {
  if (typeof value !== 'object' || value === null) return {};
  const out: Record<string, boolean> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === true) out[key] = true;
  }
  return out;
};

/** Список кодов: чужой мусор и повторы отбрасываются, порядок не важен. */
const codes = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const one of value) {
    if (typeof one === 'string' && one !== '' && !out.includes(one)) out.push(one.slice(0, 32));
  }
  return out;
};

const positions = (value: unknown): Record<string, RulePos> => {
  if (typeof value !== 'object' || value === null) return {};
  const out: Record<string, RulePos> = {};
  for (const [code, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === 'hide' || raw === 'mark' || raw === 'off') out[code] = raw;
  }
  return out;
};

/**
 * Достроить профиль из чего угодно.
 *
 * Всё непонятое заменяется умолчанием, а не отбрасывается вместе с профилем:
 * одно испорченное поле не имеет права стоить человеку всей его настройки. Так
 * же читается и профиль, приехавший строкой от товарища с другой версией.
 */
export function normalize(raw: unknown): Profile {
  const p = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_PROFILE;
  const audio = p.audio && typeof p.audio === 'object' ? p.audio as Record<string, unknown> : null;
  const name = typeof p.name === 'string' && p.name.trim() !== '' ? p.name.trim() : d.name;
  return {
    ...(audio ? { audio: { volume: volumeOf(audio.volume), tone: toneOf(audio.tone), scale: scaleOf(audio.scale) } } : {}),
    name: name.slice(0, 40),
    bank: Math.max(0, num(p.bank, d.bank)),
    roundStep: isRoundStep(p.roundStep) ? p.roundStep : d.roundStep,
    roundDir: one(p.roundDir, ['down', 'near'] as const, d.roundDir),
    roundWho: one(p.roundWho, ['book', 'coarse'] as const, d.roundWho),
    fx: Math.max(0, num(p.fx, d.fx)),
    tempo: Math.max(0, num(p.tempo, d.tempo)),
    minRub: num(p.minRub, d.minRub),
    minRoi: TRANSFERS ? Math.max(-5, Math.min(0, num(p.minRoi, -5))) : Math.max(-5, num(p.minRoi, d.minRoi)),
    maxRoi: TRANSFERS ? Math.max(0, Math.min(1, num(p.maxRoi, 1))) : Math.max(0, num(p.maxRoi, d.maxRoi)),
    mine: bool(p.mine, d.mine),
    strict: one(p.strict, ['both', 'either'] as const, d.strict),
    books: flags(p.books),
    rules: positions(p.rules),
    perMin: Math.max(0, num(p.perMin, d.perMin)),
    tailRub: Math.max(0, num(p.tailRub, d.tailRub)),
    minFit: Math.max(0, num(p.minFit, d.minFit)),
    capKind: one(p.capKind, ['any', 'known'] as const, d.capKind),
    curKind: one(p.curKind, ['any', 'mix', 'out', 'rub'] as const, d.curKind),
    hardKill: Math.max(0, num(p.hardKill, d.hardKill)),
    // Старый профиль нёс одно число на лайв и прематч. Нет своего поля —
    // то же, что hardKill: иначе прематч внезапно получил бы 90 с поверх
    // сохранённых трёхсот.
    hardKillPre: Math.max(
      0,
      Object.prototype.hasOwnProperty.call(p, 'hardKillPre')
        ? num(p.hardKillPre, d.hardKillPre)
        : num(p.hardKill, d.hardKill),
    ),
    life: Math.max(0, num(p.life, d.life)),
    still: Math.max(0, num(p.still, d.still)),
    stillSure: one(p.stillSure, ['show', 'hide'] as const, d.stillSure),
    link: one(p.link, ['no', 'either', 'both'] as const, d.link),
    prec: one(p.prec, ['any', 'coupon', 'match', 'site'] as const, d.prec),
    legsFirst: one(p.legsFirst, ['auto', 'poly'] as const, d.legsFirst),
    polyOrder: asPolyOrder(p.polyOrder, d.polyOrder),
    witness: Math.max(0, num(p.witness, d.witness)),
    edgePp: Math.max(0, num(p.edgePp, d.edgePp)),
    lead: bool(p.lead, d.lead),
    liveMode: one(p.liveMode, ['both', 'live', 'pre'] as const, d.liveMode),
    sort: one(p.sort, ['money', 'age', 'life'] as const, d.sort),
    sameBook: one(p.sameBook, ['hide', 'show'] as const, d.sameBook),
    collapse: bool(p.collapse, d.collapse),
    offSports: codes(p.offSports),
    offMarkets: codes(p.offMarkets),
    skipLiveStretch: bool(p.skipLiveStretch, d.skipLiveStretch),
    offSpans: codes(p.offSpans).filter((one) => 'MPSQHI'.includes(one)),
    sound: bool(p.sound, d.sound),
    notify: bool(p.notify, d.notify),
    alarmRub: Math.max(0, num(p.alarmRub, d.alarmRub)),
    quiet: bool(p.quiet, d.quiet),
  };
}

/** Сохранённый профиль. `null` — человек снял его сам, лента без возрастного ножа. */
function readLegacyProfile(): Profile | null {
  try {
    if (localStorage.getItem(SKIP_KEY) === '1') return null;
    const raw = localStorage.getItem(KEY);
    if (raw === null) {
      // Пустой склад — кран: найденное видно, вкус не применён. «Работа»
      // — именованный профиль, его выбирают явно.
      return null;
    }
    const loaded = normalize(JSON.parse(raw));
    const mixRubOnce =
      isWorkRubLegacy(loaded) &&
      (typeof localStorage === 'undefined' || localStorage.getItem(WORK_FX_KEY) !== '1');
    if (isOldWorkAge(loaded) || mixRubOnce) {
      const aged = normalize({
        ...loaded,
        ...(isOldWorkAge(loaded)
          ? { hardKill: 120, hardKillPre: 120, link: 'no', prec: 'any' }
          : {}),
        curKind: mixRubOnce ? 'mix' : loaded.curKind,
      });
      try {
        if (mixRubOnce) localStorage.setItem(WORK_FX_KEY, '1');
      } catch {
        // Приватный режим — не повод падать.
      }
      saveProfile(aged);
      return aged;
    }
    if (!isLegacyWorkFilter(loaded)) return loaded;
    // Фильтры — новые. Банк, конторы и свой порог денег — его: человек, сдвинувший
    // только «беру от», не должен потерять его вместе со старым возрастом.
    const migrated = normalize({
      ...DEFAULT_PROFILE,
      rules: { ...DEFAULT_PROFILE.rules },
      bank: loaded.bank,
      fx: loaded.fx,
      tempo: loaded.tempo,
      books: loaded.books,
      sound: loaded.sound,
      notify: loaded.notify,
      alarmRub: loaded.alarmRub,
      quiet: loaded.quiet,
      name: loaded.name,
      offSports: loaded.offSports,
      offMarkets: loaded.offMarkets,
      minRub: loaded.minRub !== 200 ? loaded.minRub : DEFAULT_PROFILE.minRub,
    });
    saveProfile(migrated);
    return migrated;
  } catch {
    // Испорченная запись не имеет права мешать открыть сканер.
    return null;
  }
}

/** Retired controls cannot be restored by old storage, imports or another tab. */
export function currentSettings(profile: Profile): Profile {
  return normalize({
    ...profile, name: 'настройки', rules: RU_FEED ? {} : { ...WORK_RULES },
    bank: profile.bank > 0 ? profile.bank : useSettings.getState().bank,
    fx: profile.fx > 0 ? profile.fx : DEFAULT_PROFILE.fx,
    perMin: 0, capKind: 'any', lead: false, witness: 0, edgePp: 0,
    life: 0, tempo: 0, quiet: false, notify: false,
    sort: profile.sort === 'life' ? 'money' : profile.sort,
  });
}

export function loadProfile(): Profile | null {
  const saved = readLegacyProfile();
  if (!saved) return null;
  let active = currentSettings(saved);
  // Retire the old factory ceiling once; subsequent explicit choices survive.
  const rangeKey = preferenceKey('forq.next.profile.gg-range-50-v1');
  let migrated = false;
  try { migrated = localStorage.getItem(rangeKey) === '1'; } catch { /* Read-only storage. */ }
  if (!TRANSFERS && !RU_FEED && !PREDICT_FEED && !migrated) {
    if (['работа', 'настройки'].includes(saved.name) && active.maxRoi === 30) {
      active = { ...active, maxRoi: 50, rules: { ...active.rules,
        ...(active.rules.odds_anomaly === 'hide' ? { odds_anomaly: 'mark' as const } : {}) } };
    }
    if (saveProfile(active)) {
      try { localStorage.setItem(rangeKey, '1'); } catch { /* Read-only storage. */ }
    }
  }
  // Read-time normalization also protects a tab when storage is read-only.
  if (JSON.stringify(active) !== JSON.stringify(saved)) saveProfile(active);
  return active;
}

export function saveProfile(profile: Profile): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(profile));
    return true;
  } catch {
    return false;
  }
}

/** Событие о смене профиля: студия и лента могут быть открыты во двух вкладках. */
export const PROFILE_EVENT = 'forq:profile';

/**
 * Действующий профиль. `null` — человек снял его в сите, лента без ножа.
 * Пустой склад больше не null: это «работа». Иначе студия обещает 11 вилок,
 * а лента показывает сотни — каденцию мы не прячем, возраста без профиля нет.
 */
export interface ProfileState {
  profile: Profile | null;
  /**
   * Сколько строк профиль убрал из ТЕКУЩЕГО кадра — его цена, а не настройка.
   * Живёт здесь, чтобы шапка могла назвать её рядом с именем, не пересчитывая
   * ленту второй раз: имя без цены отвечает «профиль такой-то» на вопрос
   * «куда делись вилки».
   */
  hidden: number;
  set: (profile: Profile | null) => void;
  noteHidden: (hidden: number) => void;
}

export const useProfile = create<ProfileState>((set, get) => ({
  profile: loadProfile(),
  hidden: 0,
  set: (profile) => set({ profile, hidden: profile === null ? 0 : get().hidden }),
  noteHidden: (hidden) => {
    if (hidden !== get().hidden) set({ hidden });
  },
}));

/**
 * Записать и разбудить всех, кто читает: свою вкладку событием, чужие — `storage`.
 *
 * Банк и курс уезжают в настройки, а не остаются в профиле копией. Два банка —
 * один в студии, другой в ленте — это два разных ответа на вопрос «сколько я
 * ставлю», и человек узнает о расхождении только по чужому кассовому чеку.
 */
export function applyProfile(profile: Profile, audio?: Profile['audio']): boolean {
  if (!(profile.bank > 0) || !(profile.fx > 0)) return false;
  profile = currentSettings({ ...profile, ...(audio ? { audio } : {}) });
  // Persist every participating store before notifying the current tab.
  // Another tab observes the profile write last and can read the complete values.
  const money = { ...useSettings.getState(), bank: profile.bank, fx: profile.fx,
    gate: Math.round(profile.tailRub), roundStep: profile.roundStep,
    roundDir: profile.roundDir, roundWho: profile.roundWho, polyOrder: profile.polyOrder };
  const alarm = { ...useAlarm.getState(), ...profile.audio, sound: profile.sound, notify: false };
  const writes: [string, string | null][] = [
    [SETTINGS_KEY, JSON.stringify(settingsRecord(money))],
    [ALARM_KEY, JSON.stringify(alarmRecord(alarm))],
    [SKIP_KEY, null],
    [KEY, JSON.stringify(profile)],
  ];
  const previous = new Map<string, string | null>();
  const written: string[] = [];
  try {
    for (const [key] of writes) previous.set(key, localStorage.getItem(key));
    for (const [key, value] of writes) {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
      written.push(key);
    }
  } catch {
    for (const key of written.reverse()) {
      try {
        const value = previous.get(key) ?? null;
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
      } catch { /* Read-only storage: no successful save is reported. */ }
    }
    return false;
  }
  useProfile.getState().set(profile);
  syncSettings(profile);
  try {
    window.dispatchEvent(new CustomEvent(PROFILE_EVENT, { detail: profile }));
  } catch {
    // Нет окна (тест на голом узле) — писать в хранилище это не мешает.
  }
  return true;
}

/** Поправить часть ручек. Профиля нет — не заводим: сито живёт и без него. */
export function applyProfilePatch(patch: Partial<Profile>): boolean {
  const current = useProfile.getState().profile;
  if (current === null || Object.keys(patch).length === 0) return false;
  return applyProfile(normalize({ ...current, ...patch }), current.audio);
}

/** Apply durable values without writing the profile again (cross-tab delivery). */
function syncSettings(profile: Profile): void {
  useSettings.setState({ bank: profile.bank, fx: profile.fx, gate: Math.round(profile.tailRub),
    roundStep: profile.roundStep, roundDir: profile.roundDir, roundWho: profile.roundWho,
    polyOrder: profile.polyOrder });
  if (profile.audio) {
    useAlarm.getState().setVolume(profile.audio.volume);
    useAlarm.getState().setTone(profile.audio.tone);
    useAlarm.getState().setScale(profile.audio.scale);
  }
  useAlarm.getState().setSound(profile.sound);
  // Уведомление спрашивает разрешение у браузера, и спросить его есть право
  // только здесь: применение профиля — это нажатие человека, а не фоновый такт.
  // Отказ браузера остаётся в `denied` и гасит переключатель честно.
  if (profile.notify !== useAlarm.getState().notify) void useAlarm.getState().setNotify(profile.notify);
}

/**
 * Снять профиль совсем.
 *
 * Отдельное действие, а не «применить умолчания»: разница видна на экране.
 * Умолчание — это тоже профиль, который что-то режет; снятый профиль не режет
 * ничего, и человек, ищущий пропавшие строки, обязан иметь дверь наружу.
 */
export function dropProfile(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.setItem(SKIP_KEY, '1');
  } catch {
    // Приватный режим — не повод падать.
  }
  useProfile.getState().set(null);
}

/**
 * Слушать чужие вкладки. Студию открывают рядом с лентой во втором окне, и
 * профиль, применённый в одном, обязан доехать до другого: иначе человек
 * настроил ленту и смотрит на ненастроенную, не понимая почему.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY && event.key !== SKIP_KEY) return;
    const profile = loadProfile();
    useProfile.getState().set(profile);
    if (profile) syncSettings(profile);
  });
  window.addEventListener(PROFILE_EVENT, (event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (detail !== undefined) useProfile.getState().set(currentSettings(normalize(detail)));
  });
}

// ── Строка обмена ────────────────────────────────────────────────────────────

/**
 * Проверки в строке сокращаются до четырёх букв кода и одной буквы положения:
 * `impo:h` — «impossible_roi прячет». Полные имена сделали бы строку длиннее
 * сообщения, в которое её вставляют, а четырёх букв хватает: коды каталога по
 * ним различимы, а неизвестный обрывок разбор просто пропускает.
 */
const POS_LETTER: Record<RulePos, string> = { hide: 'h', mark: 'm', off: 'o' };
const LETTER_POS: Record<string, RulePos> = { h: 'hide', m: 'mark', o: 'off' };

const PREFIX = 'forq1:';

/** Профиль строкой. Пустые поля не пишутся: строку читают глазами. */
export function encode(profile: Profile, codes: readonly string[] = []): string {
  const parts: string[] = [];
  const put = (key: string, value: string | number): void => {
    parts.push(`${key}=${value}`);
  };
  put('name', encodeURIComponent(profile.name));
  put('bank', profile.bank);
  if (profile.roundStep !== 100) put('rst', profile.roundStep);
  if (profile.roundDir !== 'down') put('rdir', profile.roundDir);
  if (profile.roundWho !== 'book') put('rwho', profile.roundWho);
  put('min', profile.minRub);
  put('roi', `${profile.minRoi}-${profile.maxRoi}`);
  put('kill', profile.hardKill);
  if (profile.hardKillPre > 0 && profile.hardKillPre !== profile.hardKill) {
    put('killPre', profile.hardKillPre);
  }
  put('link', profile.link);
  put('prec', profile.prec);
  if (profile.legsFirst !== 'auto') put('legs', profile.legsFirst);
  if (profile.polyOrder !== 'limit') put('pord', profile.polyOrder);
  put('line', profile.liveMode);
  put('sort', profile.sort);
  if (profile.mine) put('mine', profile.strict);
  if (profile.life > 0) put('life', profile.life);
  if (profile.still > 0) put('still', profile.still);
  if (profile.stillSure === 'hide') put('sure', 1);
  if (profile.witness > 0) put('wit', profile.witness);
  if (profile.edgePp > 0) put('edge', profile.edgePp);
  if (profile.lead) put('lead', 1);
  if (profile.minFit > 0) put('fit', profile.minFit);
  if (profile.perMin > 0) put('perm', profile.perMin);
  if (profile.tailRub > 0) put('tail', profile.tailRub);
  if (profile.capKind !== 'any') put('cap', profile.capKind);
  if (profile.curKind !== 'any') put('cur', profile.curKind);
  if (profile.collapse) put('fold', 1);
  if (profile.offSports.length > 0) put('nosport', profile.offSports.join(','));
  if (profile.offMarkets.length > 0) put('nomarket', profile.offMarkets.join(','));
  if (profile.skipLiveStretch) put('nostretch', 1);
  if (profile.offSpans.length > 0) put('nospan', profile.offSpans.join(','));
  // Канал сигнала пишется буквами: `s` — звук, `p` — уведомление, `sp` — оба.
  // Порог без канала не значит ничего, поэтому одно поле, а не два.
  if (profile.sound || profile.notify) {
    put('alarm', `${profile.alarmRub}${profile.sound ? 's' : ''}${profile.notify ? 'p' : ''}`);
  }
  if (profile.quiet) put('hush', 1);

  const books = Object.keys(profile.books)
    .filter((code) => profile.books[code])
    .sort();
  if (books.length > 0) put('books', books.join(','));

  // Список проверок берётся от вызывающего — это каталог ТЕКУЩЕГО кадра. Писать
  // все ключи `rules` было бы честнее лишь на вид: профиль тащил бы за собой
  // мёртвые коды прошлых версий и рос от каждого обмена.
  const known = codes.length > 0 ? codes : Object.keys(profile.rules).sort();
  const rules = known
    .filter((code) => profile.rules[code] !== undefined)
    .map((code) => `${code.slice(0, 4)}:${POS_LETTER[profile.rules[code] as RulePos]}`);
  if (rules.length > 0) put('rules', rules.join(','));

  return PREFIX + parts.join(';');
}

/**
 * Разобрать строку обмена.
 *
 * `null` — это не «профиль по умолчанию», а «строка не наша». Разница важна:
 * подсунуть человеку умолчание вместо того, что ему прислали, значит поменять
 * ему ленту и промолчать об этом.
 *
 * `codes` — каталог текущего кадра: по нему четырёхбуквенные обрывки в строке
 * снова становятся полными кодами. Без каталога положения проверок не читаются
 * вовсе — угадывать код мы не станем.
 */
export function decode(text: string, codes: readonly string[] = []): Profile | null {
  const raw = text.trim();
  if (!raw.startsWith(PREFIX)) return null;

  const bag: Record<string, string> = {};
  for (const chunk of raw.slice(PREFIX.length).split(';')) {
    const at = chunk.indexOf('=');
    if (at <= 0) continue;
    bag[chunk.slice(0, at)] = chunk.slice(at + 1);
  }

  const number = (key: string): number | undefined => {
    const value = Number.parseFloat(bag[key] ?? '');
    return Number.isFinite(value) ? value : undefined;
  };

  const roi = /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/.exec(bag.roi ?? '');
  const books: Record<string, boolean> = {};
  for (const code of (bag.books ?? '').split(',')) if (code !== '') books[code] = true;

  const rules: Record<string, RulePos> = {};
  for (const pair of (bag.rules ?? '').split(',')) {
    const [head, letter] = pair.split(':');
    const pos = LETTER_POS[letter ?? ''];
    if (head === undefined || head === '' || pos === undefined) continue;
    const full = codes.find((code) => code.startsWith(head));
    if (full !== undefined) rules[full] = pos;
  }

  return normalize({
    name: bag.name === undefined ? undefined : safeName(bag.name),
    bank: number('bank'),
    roundStep: number('rst'),
    roundDir: bag.rdir,
    roundWho: bag.rwho,
    minRub: number('min'),
    minRoi: roi ? Number.parseFloat(roi[1] ?? '') : Number.NaN,
    maxRoi: roi ? Number.parseFloat(roi[2] ?? '') : Number.NaN,
    hardKill: number('kill'),
    hardKillPre: number('killPre') ?? number('kill'),
    link: bag.link,
    prec: bag.prec,
    legsFirst: bag.legs,
    polyOrder: bag.pord,
    liveMode: bag.line,
    sort: bag.sort,
    mine: bag.mine !== undefined,
    strict: bag.mine,
    life: number('life'),
    still: number('still'),
    stillSure: bag.sure === undefined ? undefined : 'hide',
    witness: number('wit'),
    edgePp: number('edge'),
    lead: bag.lead !== undefined,
    minFit: number('fit'),
    perMin: number('perm'),
    tailRub: number('tail'),
    capKind: bag.cap,
    curKind: bag.cur,
    collapse: bag.fold !== undefined,
    offSports: (bag.nosport ?? '').split(',').filter((one) => one !== ''),
    offMarkets: (bag.nomarket ?? '').split(',').filter((one) => one !== ''),
    skipLiveStretch: bag.nostretch !== undefined,
    offSpans: (bag.nospan ?? '').split(',').filter((one) => one !== ''),
    // Старые строки писали в `alarm` голое число и подразумевали звук.
    sound: bag.alarm !== undefined && !bag.alarm.includes('p') ? true : bag.alarm?.includes('s'),
    notify: bag.alarm?.includes('p'),
    alarmRub: number('alarm'),
    quiet: bag.hush !== undefined,
    books,
    rules,
  });
}

// ── Профиль на ленте ─────────────────────────────────────────────────────────

/**
 * Точность входа ноги (`p` протокола) по лестнице студии, сверху вниз. Оба
 * написания приняты: два канала называют одно и то же разными словами, и
 * профиль не обязан знать, каким именно приехала строка.
 */
const STEP: Record<string, number> = {
  betslip: 3,
  coupon: 3,
  outcome: 3,
  event: 2,
  match: 2,
  venue: 1,
  site: 1,
  section: 1,
  none: 0,
};

const NEEDED: Record<Precision, number> = { any: 1, site: 1, match: 2, coupon: 3 };

const stepOf = (precision: string | undefined): number =>
  precision === undefined ? 0 : (STEP[precision] ?? 0);

/**
 * Число с провода. Строки разбираются наравне с числами: `edge` приезжает
 * строкой (десятичная дробь считается на сервере в `Decimal`), а `voices` и
 * `still` — числами, и профиль не обязан помнить, кто из них какой.
 */
const number = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Строка ленты глазами профиля.
 *
 * Правило то же, что у сита: НЕ режем по неизвестному. Строка, у которой шанса
 * дожить не измеряли, остаётся при любом требовании к этому шансу — иначе
 * пробел в данных выдаётся за ответ человека, а он даже не узнает, что вилку от
 * него скрыли.
 *
 * `rl` со стороны сервера — это положение проверки У НАС, а профиль говорит,
 * что с ней делать У СЕБЯ: `hide` прячет, `mark` и `off` оставляют. Второе не
 * ошибка и не мягкость: помеченную строку человек видит вместе с причиной и
 * решает сам, а спрятанную — не видит вовсе.
 */
export function holds(
  row: {
    row: {
      profit: number;
      roiPct: number;
      staked: number;
      lead: boolean;
      legs: {
        source: string;
        precision: string;
        cur: string;
        cap: { kind: string; value: number | null };
      }[];
      fork: Record<string, unknown>;
    };
    age: number;
  },
  p: Profile,
  locked: 'live' | 'prematch' | null = null,
): boolean {
  const fork = row.row.fork;
  const legs = row.row.legs;
  // Минусовый пол процента — это и есть порог: «от −5 %» не должно упираться
  // в «беру от 400 ₽», иначе человек ставит минус в студии, а лента пустая.
  if (TRANSFERS) {
    if (row.row.roiPct < p.minRoi || row.row.roiPct > p.maxRoi) return false;
  } else if (p.minRoi < 0) {
    if (row.row.roiPct < p.minRoi) return false;
  } else {
    if ((!RU_FEED || p.minRub !== 0) && row.row.profit < p.minRub) return false;
    if (p.minRoi > 0 && row.row.roiPct < p.minRoi) return false;
  }
  if (!TRANSFERS && p.maxRoi > 0 && row.row.roiPct > p.maxRoi) return false;
  const line = typeof fork.line === 'string' ? fork.line : '';
  const ageCap = ageCapOf(p, line);
  if (ageCap > 0 && row.age > ageCap) return false;
  if (p.minFit > 0 && row.row.staked < p.minFit) return false;

  // Прибыль в минуту: те же деньги, поделённые на темп человека. Ноль темпа —
  // вопрос не задан, а не «бесконечно быстро».
  if (!TRANSFERS && p.perMin > 0 && p.tempo > 0 && row.row.profit / (p.tempo / 60) < p.perMin) return false;

  if (p.capKind === 'known') {
    const named = legs.every((leg) => leg.cap.kind !== 'assumed' && leg.cap.value !== null);
    if (!named) return false;
  }

  if (p.curKind !== 'any') {
    const rubs = legs.filter((leg) => leg.cur === 'RUB').length;
    if (p.curKind === 'rub' && rubs < legs.length) return false;
    if (p.curKind === 'out' && rubs > 0) return false;
    if (p.curKind === 'mix' && rubs === legs.length) return false;
  }

  if (p.lead && !row.row.lead) return false;

  if (p.offSports.length > 0) {
    const sport = sportCanon(typeof fork.sport === 'string' ? fork.sport : '');
    if (sport !== '' && p.offSports.some((one) => sportCanon(one) === sport)) return false;
  }
  if (p.offMarkets.length > 0 && p.offMarkets.includes(marketFamily(fork as Fork))) return false;

  const period = marketPeriod(typeof fork.market === 'string' ? fork.market : undefined);
  // Не режем по неизвестному: нет ключа рынка — нет ответа, строка остаётся.
  if (p.skipLiveStretch) {
    const line = typeof fork.line === 'string' ? fork.line : null;
    if (line === 'live' && period !== null && isStretchPeriod(period)) return false;
  }
  if (p.offSpans.length > 0) {
    const family = periodFamily(period);
    if (family !== null && p.offSpans.includes(family)) return false;
  }

  if (p.mine) {
    const have = legs.filter((leg) => p.books[leg.source] === true).length;
    if (p.strict === 'both' ? have < legs.length : have === 0) return false;
  }

  if (p.sameBook === 'hide' && legs.length > 1) {
    const first = legs[0]?.source;
    // До оплаты сервер подставляет всем ногам одно имя (`demo.py`): по нему
    // любая пара выглядит «одной конторой». Это отсутствие имени, а не
    // совпадение площадок, и резать по нему — значит показать демо пустую ленту.
    if (first !== undefined && first !== UNNAMED_VENUE && legs.every((leg) => leg.source === first)) {
      return false;
    }
  }

  if (locked === null && p.liveMode !== 'both') {
    const line = typeof fork.line === 'string' ? fork.line : null;
    if (line !== null && (p.liveMode === 'live') !== (line === 'live')) return false;
  }

  if (p.link !== 'no') {
    const need = NEEDED[p.prec];
    const good = legs.filter((leg) => stepOf(leg.precision) >= need).length;
    if (p.link === 'both' ? good < legs.length : good === 0) return false;
  } else if (p.prec !== 'any') {
    // Точность без требования ссылки — не противоречие: «ссылка не обязательна,
    // но если есть, то на матч». Режем только те ноги, у которых вход ЕСТЬ и он
    // грубее просимого.
    const need = NEEDED[p.prec];
    if (legs.some((leg) => stepOf(leg.precision) > 0 && stepOf(leg.precision) < need)) return false;
  }

  const rules = Array.isArray(fork.rl) ? (fork.rl as { c?: unknown }[]) : [];
  for (const rule of rules) {
    if (typeof rule.c === 'string' && p.rules[rule.c] === 'hide') return false;
  }

  // Живучесть, покой, свидетели и отрыв — четыре замера НАШЕГО движка. Раньше
  // на их месте стояли поля зеркала (`live15`, `crowdBooks`, `crowdEdgePp`,
  // `jump`), которых наш протокол не несёт вовсе: четыре ручки студии стояли на
  // ленте и не делали ничего, а человек считал, что настроил.
  const life = number(fork.sv) ?? number(fork.svGroup);
  if (p.life > 0 && life !== null && life < p.life) return false;

  if (p.still > 0 || p.stillSure === 'hide') {
    // Покой считается по самой ЗАСТОЯВШЕЙСЯ ноге: вилку убивает та цена,
    // которую уже никто не двигает, а не средняя по паре.
    const stills = Array.isArray(fork.still) ? fork.still.map(number) : [];
    const known = stills.filter((one): one is number => one !== null);
    const still = known.length === stills.length && known.length > 0 ? Math.max(...known) : null;
    // «Точно ли измерен» спрашивается у ног: отсчёт с момента, как мы ногу
    // увидели, даёт оценку снизу — «стоит не меньше», а не «стоит ровно».
    const forkLegs = Array.isArray(fork.legs) ? (fork.legs as Record<string, unknown>[]) : [];
    const sure = forkLegs.length > 0 && forkLegs.every((leg) => leg.stillSure === true);
    if (p.stillSure === 'hide' && !sure) return false;
    if (p.still > 0 && still !== null && sure && still > p.still) return false;
  }

  const voices = Array.isArray(fork.voices) ? fork.voices.map(number) : [];
  const heard = voices.filter((one): one is number => one !== null);
  if (p.witness > 0 && heard.length > 0 && Math.min(...heard) < p.witness) return false;

  const edges = Array.isArray(fork.edge) ? fork.edge.map(number) : [];
  const gaps = edges.filter((one): one is number => one !== null);
  if (p.edgePp > 0 && gaps.length > 0 && Math.max(...gaps) > p.edgePp) return false;

  return true;
}

/** Стоит ли на профиле хоть что-нибудь сверх умолчаний. */
export function anyProfile(p: Profile): boolean {
  const d = DEFAULT_PROFILE;
  return (
    p.minRub !== d.minRub ||
    p.minRoi !== d.minRoi ||
    p.maxRoi !== d.maxRoi ||
    p.hardKill !== d.hardKill ||
    p.hardKillPre !== d.hardKillPre ||
    p.mine ||
    p.link !== d.link ||
    p.prec !== d.prec ||
    p.liveMode !== d.liveMode ||
    p.life > 0 ||
    p.still > 0 ||
    p.stillSure !== d.stillSure ||
    p.witness > 0 ||
    p.edgePp > 0 ||
    p.lead ||
    p.minFit > 0 ||
    p.perMin > 0 ||
    p.capKind !== d.capKind ||
    p.curKind !== d.curKind ||
    p.collapse !== d.collapse ||
    p.offSports.length > 0 ||
    p.offMarkets.length > 0 ||
    p.skipLiveStretch ||
    p.offSpans.length > 0 ||
    rulesDiffer(p.rules, d.rules)
  );
}

/**
 * Потолок возраста строки. Прематч не режем: у зеркала `age` — рождение
 * строки, часы, а не порча. Неизвестная линия тоже не режется возрастом,
 * иначе старый прематч без `line` умрёт вместе с лайвом.
 */
export function ageCapOf(p: Profile, line: string): number {
  if (line === 'live') return p.hardKill;
  return 0;
}

function rulesDiffer(have: Record<string, RulePos>, want: Record<string, RulePos>): boolean {
  const keys = new Set([...Object.keys(have), ...Object.keys(want)]);
  for (const key of keys) {
    if ((have[key] ?? want[key]) !== want[key]) return true;
  }
  return false;
}

function safeName(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    // Битая экранировка — не причина терять весь профиль.
    return raw;
  }
}

// Older builds kept money/audio in separate stores. The saved settings are canonical.
const initialSettings = useProfile.getState().profile;
if (initialSettings?.audio) useAlarm.setState(initialSettings.audio);
